import React from "react";

import AddIcon from "@mui/icons-material/Add";
import { Button, IconButton, Popover, Stack } from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { AiProcessingContent } from "components/modals/Files-categorization-modal/AiProcessedContent";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { usePrompt } from "hooks";
import { useDealActions } from "store";
import pickFiles from "utils/pick-files";

import { AiDataFilter } from "./AiDataFilter/AiDataFilter";
import { AiDataTable } from "./AiDataTable";
import { AiDataContext } from "./context/AiData-context";
import { useAiDataContextHook } from "./context/use-AIData-context-hook";
import { PopoverContent } from "./PopoverContent";
import { UploadFilesContent } from "./UploadFilesContent";

export const AiData: React.FC = () => {
  const { uploadFilesWithAiAction } = useDealActions();
  const openPrompt = usePrompt();
  const aiDataContext = useAiDataContextHook();

  // -------- Upload files ---------
  const onFileUpload = async () => {
    const fileList = await pickFiles({
      multiple: true,
      accept: "application/pdf"
    });
    if (!fileList?.length) return;
    const result = await openPrompt({
      Component: UploadFilesContent,
      props: {
        files: fileList
      }
    });

    if (result) {
      try {
        await openPrompt({
          Component: AiProcessingContent,
          props: {
            title: "Upload files",
            type: "aiCategorization",
            applyButtonText: "Continue",
            cb: async () => {
              const promises = Object.entries(result).map(([dealId, files]) => {
                return uploadFilesWithAiAction({
                  dealId: Number(dealId),
                  files: files,
                  aiExtraction: true
                });
              });
              await Promise.all(promises);
            }
          }
        });
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <>
      <AiDataContext.Provider value={aiDataContext}>
        <Stack direction="row" sx={{ height: "100%", margin: "0 2px 2px 2px", gap: "10px" }}>
          <Stack
            sx={{
              bgcolor: "white",
              borderRadius: "8px",
              padding: "16px",
              overflow: "auto",
              boxShadow: "0 1px 2px 0 #1212171a",
              width: "100%"
            }}
            gap="16px"
          >
            <Stack direction="row" justifyContent="space-between">
              <AiDataFilter />
              {aiDataContext.userHasEditPermission && (
                <Stack direction="row" gap="10px" alignItems="center">
                  {Boolean(aiDataContext.selectedParameterIds.length) && (
                    <MaterialTooltip
                      title={
                        aiDataContext.someSelectedRecommendationsAreIncomplete
                          ? "Some selected recommendations have missing required fields"
                          : ""
                      }
                      isShow={aiDataContext.someSelectedRecommendationsAreIncomplete}
                    >
                      <span>
                        <Button
                          disabled={aiDataContext.someSelectedRecommendationsAreIncomplete}
                          sx={{ width: "185px" }}
                          onClick={aiDataContext.onMultiChangeRecommendationStatus}
                          variant="contained"
                        >
                          Approve for selected
                        </Button>
                      </span>
                    </MaterialTooltip>
                  )}

                  <IconButton
                    sx={{ bgcolor: "#0A3142", ":hover": { bgcolor: "#0A3142" } }}
                    size="sm"
                    onClick={onFileUpload}
                  >
                    <AddIcon sx={{ color: "#fff" }} />
                  </IconButton>
                </Stack>
              )}
            </Stack>
            <AsyncContentMui isLoading={aiDataContext.isRecommendationsTableLoading}>
              <AiDataTable />
            </AsyncContentMui>
          </Stack>
        </Stack>

        <Popover
          open={Boolean(aiDataContext.openedCell?.anchorEl)}
          onClose={() => aiDataContext.setOpenedCell(null)}
          anchorEl={aiDataContext.openedCell?.anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left"
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "left"
          }}
          sx={{
            "& .MuiPopover-paper": {
              marginTop: "8px",
              width: "430px"
            }
          }}
        >
          <PopoverContent
            openedCell={aiDataContext.openedCell}
            setOpenedCell={aiDataContext.setOpenedCell}
            onChangeSpecificRecommendationValue={aiDataContext.onChangeSpecificRecommendationValue}
          />
        </Popover>
      </AiDataContext.Provider>
    </>
  );
};
